import { useEffect, useState } from 'react';
import { supabase } from '../../../services/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { useBusiness } from '../../../contexts/BusinessContext';

export function useDashboardData(timeRange = 'today', customDateRange = null) {
  const { user } = useAuth();
  const { business } = useBusiness();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!user?.id) {
          throw new Error('Usuario no autenticado');
        }

        if (!business?.id) {
          console.warn('No hay negocio seleccionado, no se cargarán datos financieros');
          setData({
            totalSales: 0,
            totalBultos: 0,
            trend: { sales: '+0%', bultos: '+0%' },
            chartData: { sales: [], bultos: [] },
            previousChartData: { sales: [], bultos: [] },
            payments: {
              cash: 0,
              transfer: 0,
              credit: 0,
              other: 0,
              pending: { amount: 0, count: 0 }
            }
          });
          setLoading(false);
          return;
        }

        // Determinar rango de fechas
        let startDate, endDate;
        const now = new Date();
        
        switch (timeRange) {
          case 'today':
            startDate = new Date(now.setHours(0, 0, 0, 0));
            endDate = new Date(now.setHours(23, 59, 59, 999));
            break;
          case 'yesterday':
            startDate = new Date(now.setDate(now.getDate() - 1));
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(startDate);
            endDate.setHours(23, 59, 59, 999);
            break;
          case 'week':
            endDate = new Date();
            endDate.setHours(23, 59, 59, 999);
            startDate = new Date();
            startDate.setDate(endDate.getDate() - 6);
            startDate.setHours(0, 0, 0, 0);
            break;
          case 'custom':
            if (customDateRange?.start && customDateRange?.end) {
              startDate = new Date(customDateRange.start);
              endDate = new Date(customDateRange.end);
              endDate.setHours(23, 59, 59, 999);
            }
            break;
          default:
            startDate = new Date(now.setHours(0, 0, 0, 0));
            endDate = new Date(now.setHours(23, 59, 59, 999));
        }

        // Fetch current period sales
        const { data: salesData, error: salesError } = await supabase
          .from('sales')
          .select(`
            *,
            sale_items (
              quantity,
              unit_price,
              subtotal
            )
          `)
          .eq('auth_user_id', user.id)
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString());

        if (salesError) throw salesError;

        // Fetch previous period sales
        const previousStartDate = new Date(startDate);
        const previousEndDate = new Date(endDate);
        const periodDiff = endDate.getTime() - startDate.getTime();
        previousStartDate.setTime(previousStartDate.getTime() - periodDiff);
        previousEndDate.setTime(previousEndDate.getTime() - periodDiff);

        const { data: previousSales } = await supabase
          .from('sales')
          .select(`
            *,
            sale_items (
              quantity,
              unit_price,
              subtotal
            )
          `)
          .eq('auth_user_id', user.id)
          .gte('created_at', previousStartDate.toISOString())
          .lte('created_at', previousEndDate.toISOString());

        // Process current period data
        const processDataByInterval = (sales, startDate, endDate) => {
          let intervals;
          if (timeRange === 'today') {
            intervals = 24;
          } else if (timeRange === 'week') {
            intervals = 7; // Forzar exactamente 7 días para la semana
          } else {
            intervals = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
          }

          const data = Array(intervals).fill(0).map((_, index) => {
            const date = new Date(startDate);
            if (timeRange === 'today') {
              date.setHours(date.getHours() + index);
            } else {
              date.setDate(date.getDate() + index);
            }
            return {
              date: date.toISOString(),
              amount: 0,
              quantity: 0
            };
          });

          sales?.forEach(sale => {
            const saleDate = new Date(sale.created_at);
            let index;
            if (timeRange === 'today') {
              index = saleDate.getHours();
            } else if (timeRange === 'week') {
              // Para la semana, usamos la diferencia de días directamente
              const saleDay = saleDate.getDate();
              const startDay = new Date(startDate).getDate();
              index = saleDay - startDay;
            } else {
              index = Math.floor((saleDate - startDate) / (1000 * 60 * 60 * 24));
            }
            
            if (index >= 0 && index < intervals) {
              data[index].amount += sale.total_amount || 0;
              data[index].quantity += sale.sale_items.reduce((sum, item) => sum + (item.quantity || 0), 0);
            }
          });

          return data;
        };

        const currentData = processDataByInterval(salesData, startDate, endDate);
        const previousData = processDataByInterval(previousSales, previousStartDate, previousEndDate);

        // Calculate totals
        const totalSales = salesData?.reduce((sum, sale) => sum + (sale.total_amount || 0), 0) || 0;
        const totalBultos = salesData?.reduce((sum, sale) => 
          sum + sale.sale_items.reduce((itemSum, item) => itemSum + (item.quantity || 0), 0), 
          0
        ) || 0;

        // Calculate payment totals
        const payments = {
          cash: 0,
          transfer: 0,
          credit: 0,
          other: 0,
          pending: {
            amount: 0,
            count: 0
          }
        };

        // Fetch all sales for payment data (not just within time range)
        const { data: allSales, error: allSalesError } = await supabase
          .from('sales')
          .select('*')
          .eq('auth_user_id', user.id);

        if (allSalesError) throw allSalesError;

        allSales?.forEach(sale => {
          if (sale.payment_status === 'paid' && sale.payments) {
            sale.payments.forEach(payment => {
              switch (payment.method) {
                case 'cash':
                  payments.cash += Number(payment.amount) || 0;
                  break;
                case 'transfer':
                  payments.transfer += Number(payment.amount) || 0;
                  break;
                case 'credit':
                  payments.credit += Number(payment.amount) || 0;
                  break;
                case 'other':
                  payments.other += Number(payment.amount) || 0;
                  break;
              }
            });
          } else if (sale.payment_status === 'unpaid' || sale.payment_status === 'partial') {
            payments.pending.count++;
            payments.pending.amount += (sale.total_amount - (sale.paid_amount || 0));
          }
        });

        // Fetch all financial transactions (ingresos y gastos)
        const { data: transactions, error: transactionsError } = await supabase
          .from('financial_transactions')
          .select('*')
          .eq('auth_user_id', user.id)
          .eq('business_id', business?.id)
          .in('type', ['income', 'expense'])
          .eq('status', 'active');

        if (transactionsError) throw transactionsError;

        // Aplicar las transacciones a los totales
        transactions?.forEach(transaction => {
          const amount = Number(transaction.amount);
          const method = transaction.payment_method;
          
          if (transaction.type === 'income') {
            payments[method] = (payments[method] || 0) + amount;
          } else if (transaction.type === 'expense') {
            payments[method] = (payments[method] || 0) - amount;
          }
        });

        const previousTotalSales = previousSales?.reduce((sum, sale) => sum + (sale.total_amount || 0), 0) || 0;
        const previousTotalBultos = previousSales?.reduce((sum, sale) => 
          sum + sale.sale_items.reduce((itemSum, item) => itemSum + (item.quantity || 0), 0),
          0
        ) || 0;

        // Calculate trends
        const calculateTrend = (current, previous) => {
          if (!previous || previous === 0) return current > 0 ? '+100%' : '+0%';
          const percentChange = ((current - previous) / Math.abs(previous)) * 100;
          const sign = percentChange > 0 ? '+' : '';
          return `${sign}${percentChange.toFixed(1)}%`;
        };

        setData({
          totalSales,
          totalBultos,
          trend: {
            sales: calculateTrend(totalSales, previousTotalSales),
            bultos: calculateTrend(totalBultos, previousTotalBultos)
          },
          chartData: {
            sales: currentData,
            bultos: currentData
          },
          previousChartData: {
            sales: previousData,
            bultos: previousData
          },
          payments
        });

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, business?.id, timeRange, customDateRange]);

  return { data, loading, error };
} 